import React, { useState, useRef, useEffect, useCallback } from 'react';
import Icon from '../AppIcon';

/**
 * CorporateSelect — Reusable dark-themed dropdown replacing all native <select> elements.
 * Props:
 *   value        — current selected value
 *   onChange     — (value) => void
 *   options      — [{ value, label, disabled? }]
 *   placeholder  — string shown when no value selected
 *   disabled     — boolean
 *   searchable   — boolean (shows search input when true or options > 8)
 *   className    — extra classes on wrapper
 *   style        — extra styles on wrapper
 *   size         — 'sm' | 'md' (default 'md')
 */
const CorporateSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Seleccionar...',
  disabled = false,
  searchable,
  className = '',
  style = {},
  size = 'md',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);

  const showSearch = searchable !== undefined ? searchable : options?.length > 8;

  const selectedOption = options?.find(o => String(o?.value) === String(value));

  const filtered = search
    ? options?.filter(o => o?.label?.toLowerCase()?.includes(search?.toLowerCase()))
    : options;

  const handleOpen = () => {
    if (disabled) return;
    setOpen(prev => !prev);
    setSearch('');
  };

  const handleSelect = (opt) => {
    if (opt?.disabled) return;
    onChange(opt?.value);
    setOpen(false);
    setSearch('');
  };

  const handleKeyDown = useCallback((e) => {
    if (e?.key === 'Escape') { setOpen(false); setSearch(''); }
  }, []);

  useEffect(() => {
    if (open && showSearch && searchRef?.current) {
      setTimeout(() => searchRef?.current?.focus(), 50);
    }
  }, [open, showSearch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef?.current && !wrapperRef?.current?.contains(e?.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const heights = { sm: '32px', md: '38px' };
  const fontSizes = { sm: '0.75rem', md: '0.8125rem' };

  return (
    <div
      ref={wrapperRef}
      className={`relative select-none ${className}`}
      style={{ minWidth: 0, ...style }}
      onKeyDown={handleKeyDown}
    >
      {/* Trigger */}
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        className="w-full flex items-center justify-between gap-2 px-3 rounded-xl font-oranienbaum transition-all outline-none"
        style={{
          height: heights?.[size],
          fontSize: fontSizes?.[size],
          background: disabled ? 'rgba(255,255,255,0.03)' : '#1A1A1A',
          border: open
            ? '1px solid rgba(214,239,78,0.40)'
            : '1px solid rgba(255,255,255,0.10)',
          color: disabled
            ? 'rgba(255,255,255,0.25)'
            : selectedOption
              ? 'rgba(255,255,255,0.90)'
              : 'rgba(255,255,255,0.35)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          boxShadow: open ? '0 0 0 2px rgba(214,239,78,0.08)' : 'none',
        }}
      >
        <span className="truncate text-left">
          {selectedOption ? selectedOption?.label : placeholder}
        </span>
        <Icon
          name={open ? 'ChevronUp' : 'ChevronDown'}
          size={13}
          style={{
            color: open ? '#D6EF4E' : 'rgba(255,255,255,0.30)',
            flexShrink: 0,
            transition: 'transform 0.15s',
          }}
        />
      </button>
      {/* Dropdown */}
      {open && (
        <div
          className="absolute z-50 w-full mt-1 rounded-xl overflow-hidden"
          style={{
            background: '#141414',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.60)',
            minWidth: '160px',
          }}
        >
          {/* Search */}
          {showSearch && (
            <div className="px-2 pt-2 pb-1" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center gap-2 px-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Icon name="Search" size={11} style={{ color: 'rgba(255,255,255,0.30)', flexShrink: 0 }} />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={e => setSearch(e?.target?.value)}
                  placeholder="Buscar..."
                  className="w-full bg-transparent outline-none font-oranienbaum text-white/80 py-1.5"
                  style={{ fontSize: '0.75rem' }}
                />
              </div>
            </div>
          )}

          {/* Options */}
          <div
            className="overflow-y-auto"
            style={{
              maxHeight: '220px',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255,255,255,0.15) transparent',
            }}
          >
            {filtered?.length === 0 ? (
              <div className="px-3 py-3 font-oranienbaum text-white/30 text-xs text-center">
                Sin resultados
              </div>
            ) : (
              filtered?.map((opt, idx) => {
                const isSelected = String(opt?.value) === String(value);
                return (
                  <button
                    key={`${opt?.value}-${idx}`}
                    type="button"
                    onClick={() => handleSelect(opt)}
                    disabled={opt?.disabled}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left font-oranienbaum transition-colors"
                    style={{
                      fontSize: fontSizes?.[size],
                      background: isSelected
                        ? 'rgba(214,239,78,0.10)'
                        : 'transparent',
                      color: opt?.disabled
                        ? 'rgba(255,255,255,0.20)'
                        : isSelected
                          ? '#D6EF4E' :'rgba(255,255,255,0.80)',
                      cursor: opt?.disabled ? 'not-allowed' : 'pointer',
                    }}
                    onMouseEnter={e => {
                      if (!isSelected && !opt?.disabled) {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                        e.currentTarget.style.color = 'rgba(255,255,255,0.95)';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isSelected) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = opt?.disabled ? 'rgba(255,255,255,0.20)' : 'rgba(255,255,255,0.80)';
                      }
                    }}
                  >
                    {isSelected && (
                      <Icon name="Check" size={11} style={{ color: '#D6EF4E', flexShrink: 0 }} />
                    )}
                    {!isSelected && <span style={{ width: 11, flexShrink: 0 }} />}
                    <span className="truncate">{opt?.label}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CorporateSelect;
